/**
 * Definiciones de policies expuestas en `ConfiguracionDistribucionPage`.
 * Extraídas como data module para mantener la página ≤500L (P7 split).
 *
 * Ningún cambio funcional vs versión inline previa.
 */

export interface PolicyDef {
  key: string;
  label: string;
  description: string;
  // string_array (B4.4): se edita como CSV en la UI; el backend recibe un array.
  type: "number" | "int" | "string" | "string_array";
  min?: number;
  max?: number;
  options?: readonly string[];
  group:
    | "service_levels"
    | "stockout"
    | "central_buffer"
    | "denials"
    | "criticality"
    | "subcompra"
    | "horizon"
    | "lifecycle";
}

export const POLICIES: readonly PolicyDef[] = [
  {
    key: "service_level_a",
    label: "Service level A",
    description: "Probabilidad de cubrir demanda para productos ABC=A",
    type: "number",
    min: 0.5,
    max: 0.9999,
    group: "service_levels",
  },
  {
    key: "service_level_b",
    label: "Service level B",
    description: "Probabilidad de cubrir demanda para productos ABC=B",
    type: "number",
    min: 0.5,
    max: 0.9999,
    group: "service_levels",
  },
  {
    key: "service_level_c",
    label: "Service level C",
    description: "Probabilidad de cubrir demanda para productos ABC=C",
    type: "number",
    min: 0.5,
    max: 0.9999,
    group: "service_levels",
  },
  {
    key: "stockout_min_duration_days",
    label: "Días mínimos para considerar stockout",
    description: "Racha mínima de qty=0 para registrar evento de stockout",
    type: "int",
    min: 1,
    max: 365,
    group: "stockout",
  },
  {
    key: "central_buffer_weeks",
    label: "Buffer central (semanas)",
    description: "Reserva en bodegas centrales como buffer de seguridad",
    type: "number",
    min: 0,
    max: 12,
    group: "central_buffer",
  },
  {
    // B4.4: hoy solo editable por SQL — ahora desde la UI. Define qué almacenes
    // cuentan como "central" para inv_central en la cadena de compra (C3).
    key: "central_warehouses",
    label: "Almacenes centrales (códigos MXP)",
    description:
      "Códigos MXP de los almacenes centrales que aportan inv_central a la compra, separados por coma (ej. A115, A715, A125).",
    type: "string_array",
    group: "central_buffer",
  },
  {
    key: "purchase_horizon_months",
    label: "Horizon compra (meses)",
    description: "Cuántos meses adelante se compra para tiendas con historial",
    type: "int",
    min: 1,
    max: 12,
    group: "horizon",
  },
  {
    key: "purchase_growth_pct",
    label: "Crecimiento proyectado default (%)",
    description:
      "Default global del campo Crecimiento proyectado en el dialog de forecast de compras (0 = sin crecimiento; ajustable por sesión)",
    type: "number",
    min: 0,
    max: 100,
    group: "horizon",
  },
  {
    key: "sample_success_threshold_pct",
    label: "Umbral éxito muestra (%)",
    description: "% de venta sobre la muestra para escalar a reorder",
    type: "int",
    min: 1,
    max: 100,
    group: "lifecycle",
  },
  // no_sales_exclusion_months RETIRADA en B4.3 (huérfana A-3): la exclusión por
  // inactividad la maneja lifecycle_discontinued_threshold_months (consumida).
  {
    key: "product_reactivation_months",
    label: "Meses para reactivar producto",
    description: "Producto exclido reactivado tras N meses con venta nueva",
    type: "int",
    min: 1,
    max: 60,
    group: "lifecycle",
  },
  // mfull_mode RETIRADA en B4.3 (huérfana A-4): ningún cálculo la leía. El
  // store-profile-classifier ya trata MFULL como tienda especial sin esta policy.
  {
    key: "denial_expiration_days",
    label: "Días para expirar denegación",
    description: "Tras N días sin satisfacer, denial pasa a 'expired'",
    type: "int",
    min: 1,
    max: 365,
    group: "denials",
  },
  {
    key: "max_denial_per_request_multiplier",
    label: "Múltiplo tope denial / ROP",
    description: "qty_denied no puede exceder ROP × este multiplicador",
    type: "number",
    min: 1,
    max: 10,
    group: "denials",
  },
  {
    key: "criticality_weight_alpha",
    label: "Peso α (déficit)",
    description: "Peso del déficit absoluto en fórmula de criticidad",
    type: "number",
    min: 0,
    max: 1000,
    group: "criticality",
  },
  {
    key: "criticality_weight_beta",
    label: "Peso β (denials)",
    description: "Peso de denegaciones acumuladas",
    type: "number",
    min: 0,
    max: 1000,
    group: "criticality",
  },
  {
    key: "criticality_weight_gamma",
    label: "Peso γ (antigüedad)",
    description: "Peso de la antigüedad del déficit",
    type: "number",
    min: 0,
    max: 1000,
    group: "criticality",
  },
  {
    key: "criticality_weight_delta",
    label: "Peso δ (rotación)",
    description: "Peso de la rotación del producto",
    type: "number",
    min: 0,
    max: 1000,
    group: "criticality",
  },
  {
    key: "criticality_weight_epsilon",
    label: "Peso ε (margen)",
    description: "Peso del margen del producto",
    type: "number",
    min: 0,
    max: 1000,
    group: "criticality",
  },
  {
    key: "criticality_weight_zeta",
    label: "Peso ζ (cobertura)",
    description: "Peso de la cobertura DCI del producto",
    type: "number",
    min: 0,
    max: 1000,
    group: "criticality",
  },
  {
    key: "criticality_threshold_critical",
    label: "Umbral crítico",
    description: "Score por encima de este valor → marca producto como crítico",
    type: "number",
    min: 0,
    max: 100000,
    group: "criticality",
  },
  {
    key: "criticality_threshold_alert",
    label: "Umbral alerta",
    description: "Score por encima de este valor → emite alerta",
    type: "number",
    min: 0,
    max: 100000,
    group: "criticality",
  },
  {
    key: "capacity_tolerance_factor",
    label: "Factor tolerancia capacidad",
    description: "Factor multiplicador sobre capacidad antes de bloquear",
    type: "number",
    min: 1,
    max: 3,
    group: "central_buffer",
  },
  {
    key: "diff_reason_expiration_days",
    label: "Días expiración razón diff",
    description: "Tras N días sin captura, diff pasa a 'sin_razon'",
    type: "int",
    min: 1,
    max: 90,
    group: "subcompra",
  },
  {
    key: "subcompra_alert_threshold_pct",
    label: "% subcompra para alertar",
    description: "% de productos con cobertura insuficiente que dispara alerta",
    type: "number",
    min: 0,
    max: 100,
    group: "subcompra",
  },
  {
    key: "subcompra_correction_factor",
    label: "Factor corrección subcompra",
    description:
      "Multiplicador manual de la cantidad de compra pre-pack (1.0 = sin ajuste). Restaurado 2026-06-09 por decisión del propietario.",
    type: "number",
    min: 1,
    max: 5,
    group: "subcompra",
  },
];

export const GROUP_LABELS: Record<PolicyDef["group"], string> = {
  service_levels: "Niveles de servicio (ABC)",
  stockout: "Detección de stockout",
  central_buffer: "Buffer central / capacidad",
  denials: "Denegaciones",
  criticality: "Criticidad — pesos y umbrales",
  subcompra: "Sub-compra y diferencias",
  horizon: "Horizonte de compra",
  lifecycle: "Ciclo de vida del producto",
};
